import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  Image,
  Linking,
  NativeModules,
} from 'react-native';
import { useNavigation } from '@react-navigation/native';
import Config from 'react-native-config';
import { useDispatch } from 'react-redux';
import { v1 as uuidv1 } from 'uuid';
import { CometChatUIKit } from '../../libraries/ReactNativeCometChatUiKit';
import DynamicLinkManager from '../../utils/DynamicLinkManager';
import { navigateToScreen } from '../../utils/NavigationUtility';
import colors from '../../theme/Colors';
import size from '../../theme/Fonts';
import images from '../../theme/Images';
import Share from '../../libraries/ReactNativeShare';
import RemoteConfig from '../../utils/RemoteConfig';
import {
  AddressBeforeLoginAppConfig,
  CURRENT_GUEST_STATE,
  EnableMandatoryLogin,
  REMOTE_CONFIG_KEYS,
  TRACKING_PARAMS,
} from '../../config/Constants';
import {
  isAndroid,
  isBlank,
  isIOS,
  isPresent,
  isWeb,
} from '../../utils/BooleanUtility';
import ContactsUsUtility from '../../utils/ContactUsUtility';
import NavigationService from '../../navigator/NavigationService';
import { getStoreRef } from '../../store/StoreUtility';
import FoxyAlert from '../../components/camera/shared/FoxyAlert';
import Utility from '../../utils/Utility';
import {
  unlinkDevice,
  saveUserState,
  isUserLoggedOutOnce,
  changeLoginModalState,
  logoutUser,
  smsRetrieverStatus,
  registerGuestUser,
  saveGuestAuthToken,
  setPhoneNumberModalInfo,
} from '../../actions/LoginActions';
import { exitStoreMode } from '../../actions/RetailStoreActions';
import { AnalyticsManager } from '../../analytics';
import NotificationManager from '../../utils/NotificationsManager';
import AppConfig from '../../config/AppConfig';
import AnalyticsUtility from '../../analytics/AnalyticsUtility';
import BGNetworkTaskManager from '../../utils/BGNetworkTaskManager';
import Keychain from '../../libraries/ReactNativeKeychain';
import { clearAllCacheData } from '../../utils/cacheUtils';

const DefaultSizeAppLinkList = ({ itemData = {} }) => {
  const {
    display_name = '',
    destination_url = '',
    app_link_type = '',
  } = itemData;
  const navigation = useNavigation();
  const dispatch = useDispatch();
  const [inviteLink, setInviteLink] = useState();
  const [logOutModal, showLogoutModal] = useState(false);
  const authToken = getStoreRef().getState()?.UserAccountInfo?.authToken;
  const scheduledNotificationIds =
    getStoreRef()?.routines?.scheduledNotificationIds;
  const cometChatUid =
    getStoreRef().getState()?.UserAccountInfo?.cometChatUserInfo?.data
      ?.attributes?.uid;
  const isInternationalChoiceEnabled = AppConfig.getBooleanValue(
    Config.ENABLE_INTERNATIONAL_CHOICE,
  );

  useEffect(() => {
    if (app_link_type === 'share_app') {
      generateDynamicShortLink();
    }
  }, [app_link_type]);

  const generateDynamicShortLink = async () => {
    const linkOptions = {
      title: Config.APP_NAME,
      description: RemoteConfig.getValue(REMOTE_CONFIG_KEYS.share_app_text),
      trackingParams: TRACKING_PARAMS.SHARE.GENERIC,
    };

    try {
      const shortLink = await DynamicLinkManager.getShortenUrl(
        `${Config.WEB_URL}/`,
        linkOptions,
        true,
      );
      setInviteLink(shortLink);
    } catch (error) {
      console.tron.log('Error generating short URL:', error);
    }
  };

  const shareApp = () => {
    const shareText = RemoteConfig.getValue(REMOTE_CONFIG_KEYS.share_app_text);
    let appInviteLink = inviteLink;
    if(!appInviteLink){
      if(isAndroid()){
        appInviteLink = isPresent(Config.PLAY_STORE_LINK) && Config.PLAY_STORE_LINK;
      }
      if(isIOS()){
        appInviteLink = isPresent(Config.APP_STORE_LINK) && Config.APP_STORE_LINK;
      }
    }
    const shareOptions = {
      title: `Share ${Config.APP_NAME} App Using`,
      message: `${shareText} ${appInviteLink}`,
    };

    Share.open(shareOptions).catch(() => {
      console.tron.log('Sharing failed or cancelled.');
    });
  };

  const handleNavigation = () => {
    DynamicLinkManager.handleLinkWithInternalTrackingParams(
      destination_url,
      ({ route, slug, extra }) => {
        navigateToScreen({
          navigation,
          type: 'push',
          screen: route,
          params: { slug, extra },
        });
      },
    );
  };

  const handlePress = () => {
    if (isBlank(destination_url)) {
      switch (app_link_type) {
        case 'share_app':
          return shareApp();
        case 'open_whatsapp':
          return ContactsUsUtility.onContactUsOptionPress();
        case 'app_permission':
          return Linking.openSettings();
        case 'logout':
          return showLogoutModal(true);
        case 'login':
          dispatch(
            setPhoneNumberModalInfo(Utility.getLoginModalInfo('VIEW_PROFILE')),
          );
          return NavigationService.renderOnboarding({
            furtherAction: clearAllCacheData,
          });
        default:
          return;
      }
    }

    if (display_name === 'Invite Center' && isBlank(authToken)) {
      dispatch(
        setPhoneNumberModalInfo(Utility.getLoginModalInfo('VIEW_INVITES')),
      );
      return NavigationService.renderOnboarding({
        furtherAction: () => handleNavigation(destination_url),
      });
    }

    handleNavigation();
  };

  const generateGuestTokenBeforeLogout = () => {
    const guestToken = uuidv1();
    dispatch(saveGuestAuthToken(guestToken));
    BGNetworkTaskManager.saveGuestAuthToken(guestToken);
    dispatch(registerGuestUser());
    AnalyticsUtility.setGuestTokenAsProperties(guestToken);
  };

  const cancelScheduledNotifications = () => {
    if (isAndroid()) {
      NativeModules.ScheduledLocalNotification.cancelAllScheduledNotifications();
    } else if (isIOS() && isPresent(scheduledNotificationIds)) {
      NativeModules.LocalNotificationHandler.cancelScheduledNotifications(
        scheduledNotificationIds,
      );
    }
  };

  const logoutCometChatUser = () => {
    CometChatUIKit.logout({ cometChatUid })
      .then((user) => {
        console.tron.log(user, 'cometChatUserLogout');
        console.tron.log('User loggedOut in successfully  (user.getName())');
      })
      .catch((error) => {
        console.tron.log('Login failed with exception:', error);
      });
  };

  const deleteAuthTokenFromDevice = async () => {
    if (isWeb()) return;
    try {
      await Keychain.resetGenericPassword();
      console.log('Token deleted successfully');
    } catch (error) {
      console.error('Error deleting token', error);
    }
  };

  const confirmLogout = () => {
    showLogoutModal(false);
    Utility.haltOffersLandingFlow = false;
    dispatch(saveUserState(CURRENT_GUEST_STATE.INITIAL));
    dispatch(unlinkDevice());
    dispatch(isUserLoggedOutOnce(true));
    dispatch(changeLoginModalState(''));
    dispatch(logoutUser());
    logoutCometChatUser();
    deleteAuthTokenFromDevice();
    dispatch(smsRetrieverStatus('initiate'));
    dispatch(exitStoreMode());
    AnalyticsManager.resetUserProperties();
    NotificationManager.cancelCartNotification();
    generateGuestTokenBeforeLogout();
    cancelScheduledNotifications();
    clearAllCacheData();
    setTimeout(() => {
      const { routes = [] } = navigation.getState();
      const tabNavigatorState = routes[routes.length - 2]?.state || {};
      const {
        routeNames: tabNavigatorRouteNames = [],
        index: selectedTabIndex = '',
      } = tabNavigatorState;
      const isPreviousScreenChat =
        tabNavigatorRouteNames[selectedTabIndex] || '';
      if (
        AppConfig.getBooleanValue(Config.DISABLE_PROFILE_STATIC_HEADER) ||
        EnableMandatoryLogin ||
        isInternationalChoiceEnabled ||
        AddressBeforeLoginAppConfig ||
        isPreviousScreenChat
      ) {
        navigation.navigate('Feed');
      } else {
        navigation.navigate('TabNavigator');
      }
      if (Utility.isAndroid()) {
        NativeModules.UserPreferences.resetUserPreferences();
      }
    }, 700);
  };

  if (
    (isBlank(authToken) && display_name === 'Logout') ||
    (isPresent(authToken) && display_name === 'Login') || (
      (display_name === 'Account Settings' ||
        display_name === 'Share App' ||
        display_name === 'Notifications' ||
        display_name === 'Saved Payment Methods') &&
        isWeb()
    )
  ) {
    return null;
  }

  return (
    <>
      <View style={styles.sidePadding}>
        <View style={styles.cardLayoutContainer}>
          <TouchableOpacity onPress={handlePress}>
            <View style={styles.cardsItem}>
              <Text style={styles.menuCardItemTextStyle}>{display_name}</Text>
              <Image
                source={images.chevronRight}
                style={styles.cardItemsImage}
              />
            </View>
          </TouchableOpacity>
        </View>
      </View>
      <FoxyAlert
        isVisible={logOutModal}
        alertBoxTitle='Logout?'
        alertMessage='Are you sure you want to logout.'
        textAlignment='center'
        firstButtonTitle='Yes'
        firstButtonTextColor={colors.dodgerBluePressed}
        secondButtonTitle='Cancel'
        firstButtonOnPress={confirmLogout}
        secondButtonOnPress={() => showLogoutModal(false)}
      />
    </>
  );
};

const styles = StyleSheet.create({
  sidePadding: {
    paddingHorizontal: 12,
  },
  cardLayoutContainer: {
    backgroundColor: colors.white,
    borderWidth: 0.5,
    borderColor: colors.borderDark,
  },
  menuCardItemTextStyle: {
    color: colors.foxyBlack,
    fontFamily: 'Roboto-Regular',
    fontSize: size.h4,
  },
  cardsItem: {
    padding: 16,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  cardItemsImage: {
    tintColor: colors.silver,
    marginLeft: 10,
  },
});

export default DefaultSizeAppLinkList;
